// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The in-headset dock — the control panel under the globe.
 *
 * AYNI (2026-10-04, the AR refresh): the first HUD was a 0.6 m strip of
 * five unlabelled icons (play, mute, title, browse, exit). The dock that
 * replaced it has three rows, every button labelled:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Sea Surface Temperature                    2 Oct 2026 12:00 │  title, date
 *   │ [⏮] [ ▶ ] [⏭] [🔊] [1×]  ════════●══════════════════        │  transport
 *   │ [Browse] [Layers] [Data] [View] [Spin] [Help] [Exit]        │  actions
 *   └──────────────────────────────────────────────────────────────┘
 *
 * AYNI (owner, 2026-10-22): Spin is the auto-rotate — each press goes
 * round ½×, 1×, 2×, 4× and off, the button lit and labelled with the
 * rate while the globe turns (vrSpin.ts; the session applies it).
 *
 * The progress bar shows for a video with no declared time axis; a
 * stream with one gets the date track under the dock instead
 * (vrTimelineTrack), so the two never say the same thing twice. A notice
 * (a stream outside the plan, a meeting's presenter driving) takes the
 * title row for a few seconds.
 *
 * Headsets only: a phone draws its controls in the DOM overlay
 * (src/ui/xrPhoneChrome.ts) and hides this mesh. The dock's mic talks to
 * the AYNI chatbot; AYNI (owner on Quest, 2026-11-04) its words stand in a
 * window of their own beside the globe (`vrChatPanel.ts`): the caption strip
 * that hung under the dock was hidden behind the date track.
 *
 * `dockLayout` is the pure layout (canvas pixels), shared by the drawing
 * and the hit test so the two cannot disagree.
 */

import type * as THREE from 'three'
import { t } from '../i18n'
import { createCanvasPanel, PANEL_COLORS, type PanelIcon, type PanelPainter } from './vrPanelKit'
import { spinRateText } from './vrSpin'

/** World size of the dock; 1 canvas pixel ≈ 0.55 mm. */
export const HUD_WIDTH = 0.77
export const HUD_HEIGHT = 0.24

/** Canvas resolution, in the plane's aspect. */
const CANVAS_WIDTH = 1400
const CANVAS_HEIGHT = 436

const PAD = 36
const TITLE_Y = 58
const TRANSPORT_TOP = 104
const TRANSPORT_H = 116
const ACTIONS_TOP = 252
const ACTIONS_H = 156
const GAP = 16


export type VrHudAction =
  | 'play-pause'
  | 'step-back'
  | 'step-forward'
  | 'mute'
  | 'speed'
  | 'seek'
  | 'voice'
  | 'browse'
  | 'layers'
  | 'data'
  | 'view'
  | 'spin'
  /** AYNI (owner, 2026-11-04): Solar System mode's page. */
  | 'solar'
  | 'help'
  | 'exit-vr'

/** Which side panel is open (vrSidePanel), for the matching button's highlight. */
export type VrPanelKind = 'browse' | 'layers' | 'data' | 'view' | 'help' | 'solar'

/**
 * The assistant's voice turn as the dock draws it. `idle` with a caption
 * is a finished reply that is still lingering.
 */
export type VrVoicePhase = 'idle' | 'listening' | 'thinking' | 'speaking' | 'error'
export interface VrVoiceState {
  phase: VrVoicePhase
  caption: string
  /** AYNI (2026-11-04): with a reply, the question it answers (the chatbot window shows both). */
  question?: string
  /** No voice could read the reply out: the window says so. */
  unread?: boolean
}

export interface VrHudState {
  /** Title on the first row. Null/empty says how to choose one. */
  datasetTitle: string | null
  isPlaying: boolean
  /** A video is loaded: the transport row has its buttons. */
  hasVideo: boolean
  isMuted: boolean
  /** Panels in the 2D layout (1/2/4); more than one draws a dot per globe. */
  panelCount: number
  primaryIndex: number
  /** Kept for callers of the first HUD: true while Browse is open. */
  browseOpen: boolean
  /** The side panel that is open, if any; wins over `browseOpen`. */
  openPanel?: VrPanelKind | null
  /** Unused by the dock since the readout moved to the ray's tip (vrReadout); kept for its callers. */
  probeReadout?: string | null
  /** The date the globe shows, as the 2D date label says it. */
  dateLabel?: string | null
  /** Playback rate (1 = normal). */
  speed?: number
  /** Where a video without a time axis is, 0..1; null hides the bar. */
  progress?: number | null
  /** A meeting's presenter has the controls: the transport is greyed and refuses. */
  locked?: boolean
  /** A short message over the title row (a refused action, a locked stream). */
  notice?: string | null
  /** The assistant's voice turn; null/absent hides the mic. */
  voice?: VrVoiceState | null
  /** AYNI (owner, 2026-10-22): the auto-rotate's rate (vrSpin.ts), 0 or absent while the globe is still. */
  spin?: number
  /** AYNI (Quest review B2): the video waits for data; the dock says so in the date's place. */
  buffering?: boolean
}

/** A pressable area of the dock, in canvas pixels. */
export interface DockRect {
  x: number
  y: number
  w: number
  h: number
  action: VrHudAction
}

interface DockButton extends DockRect {
  icon?: PanelIcon
  label?: string
}

const ACTIONS: ReadonlyArray<{ action: VrHudAction; icon: PanelIcon }> = [
  { action: 'browse', icon: 'browse' },
  { action: 'layers', icon: 'layers' },
  { action: 'data', icon: 'pin' },
  { action: 'view', icon: 'view' },
  // AYNI (owner, 2026-10-22): the auto-rotate, round its speeds with each press (vrSpin.ts).
  { action: 'spin', icon: 'spin' },
  // AYNI (owner, 2026-11-04): Solar System mode (services/solarSystem/vrSolarPanel.ts).
  { action: 'solar', icon: 'solar' },
  { action: 'help', icon: 'help' },
  { action: 'exit-vr', icon: 'exit' },
]

/** The progress bar's track, when the dock shows one. */
export function dockProgressRect(state: VrHudState): { x: number; y: number; w: number; h: number } | null {
  if (!state.hasVideo || state.progress === null || state.progress === undefined) return null
  const x = transportEnd(state) + 28
  const w = CANVAS_WIDTH - PAD - x
  if (w < 120) return null
  return { x, y: TRANSPORT_TOP, w, h: TRANSPORT_H }
}

/** Where the transport buttons end, in canvas x. */
function transportEnd(state: VrHudState): number {
  let x = PAD
  for (const b of transportButtons(state)) x = b.x + b.w
  return x
}

function transportButtons(state: VrHudState): DockButton[] {
  if (!state.hasVideo) return []
  const out: DockButton[] = []
  let x = PAD
  const y = TRANSPORT_TOP
  const add = (action: VrHudAction, w: number, icon?: PanelIcon): void => {
    out.push({ x, y, w, h: TRANSPORT_H, action, icon })
    x += w + GAP
  }
  add('step-back', 116, 'step-back')
  add('play-pause', 140, state.isPlaying ? 'pause' : 'play')
  add('step-forward', 116, 'step-forward')
  add('mute', 116, state.isMuted ? 'muted' : 'sound')
  add('speed', 128)
  if (state.voice) add('voice', 116)
  return out
}

/**
 * Every pressable area of the dock for a state, in canvas pixels. Pure:
 * the drawing and the hit test both read it.
 */
export function dockLayout(state: VrHudState): DockRect[] {
  const rects: DockRect[] = [...transportButtons(state)]
  const bar = dockProgressRect(state)
  if (bar) rects.push({ ...bar, action: 'seek' })
  const count = ACTIONS.length
  const w = (CANVAS_WIDTH - PAD * 2 - GAP * (count - 1)) / count
  ACTIONS.forEach((a, i) => {
    rects.push({ x: PAD + i * (w + GAP), y: ACTIONS_TOP, w, h: ACTIONS_H, action: a.action })
  })
  return rects
}

/** The transport refuses while a presenter drives; the panels and exit never do. */
function isTransport(action: VrHudAction): boolean {
  return action === 'play-pause' || action === 'step-back' || action === 'step-forward'
    || action === 'speed' || action === 'seek'
}

/**
 * The dock action under a plane UV (v grows upward), or null. Pure, so
 * the layout is testable without a canvas.
 */
export function hudActionAt(state: VrHudState, uv: { x: number; y: number }): VrHudAction | null {
  if (uv.x < 0 || uv.x > 1 || uv.y < 0 || uv.y > 1) return null
  const px = uv.x * CANVAS_WIDTH
  const py = (1 - uv.y) * CANVAS_HEIGHT
  for (const r of dockLayout(state)) {
    if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) {
      if (state.locked && isTransport(r.action)) return null
      return r.action
    }
  }
  return null
}

/** Where along the progress bar a UV falls, 0..1, or null off the bar. */
export function hudSeekFraction(state: VrHudState, uv: { x: number; y: number }): number | null {
  const bar = dockProgressRect(state)
  if (!bar) return null
  const px = uv.x * CANVAS_WIDTH
  return Math.max(0, Math.min(1, (px - bar.x) / bar.w))
}

/** The rate after `speed` is pressed: round the ladder the browser's speed bar offers (playbackSpeed.ts). */
export { SPEED_STEPS, nextSpeed } from './playbackSpeed'

function speedLabel(speed: number): string {
  const n = Number.isInteger(speed) ? String(speed) : speed.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
  return t('vr.dock.speed', { speed: n })
}

/**
 * What the chatbot window says for a voice state (`vrChatPanel.ts`) — a
 * short label and the text under it — or null when there's nothing to show
 * and the window hides. Pure for the same reason as {@link hudActionAt}.
 */
export function voiceCaption(voice: VrVoiceState | null | undefined): { label: string; text: string } | null {
  if (!voice) return null
  switch (voice.phase) {
    case 'listening':
      return { label: t('vr.voice.listening'), text: voice.caption || t('vr.voice.listeningHint') }
    case 'thinking':
      return { label: t('vr.voice.thinking'), text: voice.caption }
    case 'speaking':
      return { label: t('vr.voice.speaker'), text: voice.caption }
    case 'error':
      return { label: t('vr.voice.speaker'), text: t('vr.voice.error') }
    case 'idle':
      return voice.caption ? { label: t('vr.voice.speaker'), text: voice.caption } : null
  }
}

export interface VrHudHandle {
  /** The dock's mesh — add to the scene. */
  readonly mesh: THREE.Mesh
  /** Update what it shows; redraws only when something it draws changed. */
  setState(state: VrHudState): void
  /** The action under a raycast UV, or null on the panel's empty parts. */
  hitTest(uv: { x: number; y: number }): VrHudAction | null
  /** Where along the progress bar a UV falls, or null. */
  seekFraction(uv: { x: number; y: number }): number | null
  dispose(): void
}

const ACTION_LABEL: Record<string, Parameters<typeof t>[0]> = {
  browse: 'vr.dock.browse',
  layers: 'vr.dock.layers',
  data: 'vr.dock.data',
  view: 'vr.dock.view',
  spin: 'vr.dock.spin',
  solar: 'vr.dock.solar',
  help: 'vr.dock.help',
  'exit-vr': 'vr.dock.exit',
}

/** AYNI: the largest label size (28 down to 18) at which `label` fits `width`. */
function dockLabelSize(p: PanelPainter<VrHudAction>, label: string, width: number): number {
  for (let size = 28; size > 18; size -= 2) {
    p.g.font = p.font(size, 600)
    if (p.g.measureText(label).width <= width) return size
  }
  return 18
}

/** AYNI: the Spin button's label — "Spin" still, "Spin 1×" while it turns the globe. */
function spinLabel(rate: number | undefined): string {
  return rate && rate > 0 ? t('vr.dock.spinRate', { rate: spinRateText(rate) }) : t('vr.dock.spin')
}

function drawDock(p: PanelPainter<VrHudAction>, state: VrHudState): void {
  p.frame(30)
  const open = state.openPanel ?? (state.browseOpen ? 'browse' : null)

  // --- Title row: the dataset and its date, or a notice over both.
  if (state.notice) {
    // AYNI (Quest review B2): two lines (a notice was cut with "…" on one), smaller only when one line won't do.
    const maxWidth = CANVAS_WIDTH - PAD * 2 - 24
    const fits = p.measure(state.notice, 30, 600) <= maxWidth
    const lines = fits ? [state.notice] : p.wrap(state.notice, maxWidth, 2, 24, 600)
    const size = fits ? 30 : 24
    p.box(PAD - 12, TITLE_Y - 40, CANVAS_WIDTH - PAD * 2 + 24, 80, 18, 'rgba(255, 204, 102, 0.16)', PANEL_COLORS.warning, 2)
    lines.forEach((line, i) => {
      const y = lines.length === 1 ? TITLE_Y : TITLE_Y - 15 + i * 30
      p.text(line, CANVAS_WIDTH / 2, y, { size, weight: 600, color: PANEL_COLORS.warning, align: 'center' })
    })
  } else {
    const date = state.buffering ? t('vr.dock.buffering') : state.dateLabel ?? ''
    const dateW = date ? p.measure(date, 30, 600) : 0
    if (date) p.text(date, CANVAS_WIDTH - PAD, TITLE_Y, { size: 30, weight: 600, align: 'right', color: PANEL_COLORS.text })
    const title = state.datasetTitle || t('vr.dock.noDataset')
    p.text(title, PAD, TITLE_Y, {
      size: 36,
      weight: 650,
      color: state.datasetTitle ? PANEL_COLORS.text : PANEL_COLORS.textSecondary,
      maxWidth: CANVAS_WIDTH - PAD * 2 - dateW - (date ? 40 : 0),
    })
  }

  // One dot per globe when the 2D layout has several, the primary lit.
  if (state.panelCount > 1) {
    const spacing = 24
    const start = CANVAS_WIDTH / 2 - ((state.panelCount - 1) * spacing) / 2
    for (let i = 0; i < state.panelCount; i++) {
      p.g.beginPath()
      p.g.arc(start + i * spacing, 14, 6, 0, Math.PI * 2)
      p.g.fillStyle = i === state.primaryIndex ? PANEL_COLORS.accent : 'rgba(232, 234, 240, 0.35)'
      p.g.fill()
    }
  }

  // --- Transport row.
  if (!state.hasVideo) {
    p.text(t('vr.dock.still'), PAD, TRANSPORT_TOP + TRANSPORT_H / 2, { size: 26, color: PANEL_COLORS.muted })
  }
  for (const b of transportButtons(state)) {
    const disabled = !!state.locked && isTransport(b.action)
    if (b.action === 'speed') {
      p.button(b.x, b.y, b.w, b.h, speedLabel(state.speed ?? 1), b.action, { disabled, size: 32 })
    } else if (b.action === 'voice') {
      const listening = state.voice?.phase === 'listening'
      p.button(b.x, b.y, b.w, b.h, '', b.action, { icon: 'mic', active: listening })
    } else {
      p.button(b.x, b.y, b.w, b.h, '', b.action, {
        icon: b.icon,
        primary: b.action === 'play-pause' && !disabled,
        disabled,
        radius: b.action === 'play-pause' ? b.h / 2 : 18,
      })
    }
  }
  const bar = dockProgressRect(state)
  if (bar) {
    const midY = bar.y + bar.h / 2
    const progress = Math.max(0, Math.min(1, state.progress ?? 0))
    p.box(bar.x, midY - 7, bar.w, 14, 7, 'rgba(255, 255, 255, 0.18)')
    if (progress > 0) p.box(bar.x, midY - 7, Math.max(14, bar.w * progress), 14, 7, state.locked ? 'rgba(34,197,94,0.45)' : PANEL_COLORS.accent)
    p.g.beginPath()
    p.g.arc(bar.x + bar.w * progress, midY, 18, 0, Math.PI * 2)
    p.g.fillStyle = PANEL_COLORS.text
    p.g.fill()
    if (!state.locked) p.hit(bar.x, bar.y, bar.w, bar.h, 'seek')
  }
  if (state.locked) {
    const x = (bar ? bar.x : transportEnd(state) + 28)
    p.text(t('vr.dock.locked'), x, TRANSPORT_TOP + 18, { size: 22, color: PANEL_COLORS.warning, maxWidth: CANVAS_WIDTH - PAD - x })
  }

  // --- Actions row: every button labelled.
  for (const r of dockLayout(state)) {
    const meta = ACTIONS.find((a) => a.action === r.action)
    if (!meta) continue
    const key = ACTION_LABEL[r.action]
    const spinning = r.action === 'spin' && !!state.spin && state.spin > 0
    const label = r.action === 'spin' ? spinLabel(state.spin) : key ? t(key) : ''
    p.button(r.x, r.y, r.w, r.h, label, r.action, {
      icon: meta.icon,
      stacked: true,
      // AYNI: eight buttons since Solar System; a long label ("Solar System", "Sistema solar") steps its size down to fit.
      size: dockLabelSize(p, label, r.w - 12),
      radius: 22,
      active: spinning || (open !== null && open === r.action),
      danger: r.action === 'exit-vr',
    })
  }
}

/** Fields the dock draws; a change in any redraws it. */
function signature(s: VrHudState): string {
  return [
    s.datasetTitle ?? '', s.isPlaying, s.hasVideo, s.isMuted, s.panelCount, s.primaryIndex,
    s.openPanel ?? (s.browseOpen ? 'browse' : ''), s.dateLabel ?? '', s.speed ?? 1,
    s.progress === null || s.progress === undefined ? '-' : Math.round(s.progress * 400),
    s.locked ? 1 : 0, s.notice ?? '', s.voice?.phase ?? '',
    // AYNI (Quest review B3): Spin lights up and shows its rate on a still or paused dataset too.
    s.spin ?? 0, s.buffering ? 1 : 0,
  ].join('|')
}

/**
 * Build the dock. The caller adds `mesh` to the scene, feeds `setState`
 * each frame and disposes it on session end.
 */
export function createVrHud(THREE_: typeof THREE): VrHudHandle {
  const panel = createCanvasPanel<VrHudAction>(THREE_, {
    widthM: HUD_WIDTH,
    heightM: HUD_HEIGHT,
    canvasW: CANVAS_WIDTH,
    canvasH: CANVAS_HEIGHT,
  })
  const mesh = panel.mesh

  let state: VrHudState = {
    datasetTitle: null,
    isPlaying: false,
    hasVideo: false,
    isMuted: true,
    panelCount: 1,
    primaryIndex: 0,
    browseOpen: false,
  }
  let drawn = ''

  function redraw(): void {
    drawn = signature(state)
    panel.draw((p) => drawDock(p, state))
  }
  redraw()

  return {
    mesh,
    setState(next) {
      state = next
      if (signature(next) !== drawn) redraw()
    },
    hitTest(uv) {
      return hudActionAt(state, uv)
    },
    seekFraction(uv) {
      return hudSeekFraction(state, uv)
    },
    dispose() {
      panel.dispose()
    },
  }
}
